import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
  } from "react";
  import axios from "axios";
  import { useAuth } from "./AuthContext";
  
  const AccountSetupContext = createContext(null);
  
  const DEFAULT_CONNECTIONS = Object.freeze({
    ga: false,
    gads: false,
    meta: false,
    linkedin: false,
  });
  function getBackendUserId(user) {
    if (user?.userId) return user.userId;
    if (typeof window === "undefined") return null;
    return localStorage.getItem("mb_userId");
  }
  
  async function fetchChannelStatuses({ apiBase, userId }) {
    const API_BASE = apiBase || process.env.REACT_APP_API_URL;
    if (!API_BASE) {
      throw new Error("Missing REACT_APP_API_URL");
    }
    const params = { userId };
  
    // Keep endpoints aligned with existing usage in ChannelsView.
    const [ga, gads, meta, linkedin] = await Promise.allSettled([
      axios.get(`${API_BASE}/auth/google/status`, { params }),
      axios.get(`${API_BASE}/auth/googleads/status`, { params }),
      axios.get(`${API_BASE}/auth/meta/status`, { params }),
      axios.get(`${API_BASE}/auth/linkedin/status`, { params }),
    ]);
  
    return {
      ga: !!ga?.value?.data?.connected,
      gads: !!gads?.value?.data?.connected,
      meta: !!meta?.value?.data?.connected,
      linkedin: !!linkedin?.value?.data?.connected,
    };
  }
  
  export function AccountSetupProvider({ children }) {
    const { user } = useAuth();
  
    const [connections, setConnections] = useState(DEFAULT_CONNECTIONS);
    const [loading, setLoading] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [lastError, setLastError] = useState(null);
  
    const userId = getBackendUserId(user);
    const apiBase = process.env.REACT_APP_API_URL || "http://localhost:3001";
  
    // StrictMode-safe guard: ensure we only do the initial fetch once per userId.
    const fetchedForUserIdRef = useRef(null);
    const inFlightRef = useRef(false);
  
    const refreshConnections = useCallback(
      async ({ silent = false } = {}) => {
        if (!userId) return;
        if (inFlightRef.current) return;
  
        inFlightRef.current = true;
        if (!silent) setLoading(true);
        setLastError(null);
        try {
          const next = await fetchChannelStatuses({ apiBase, userId });
          setConnections(next);
          fetchedForUserIdRef.current = userId;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[AccountSetupContext] refreshConnections failed", err);
          setLastError(err);
        } finally {
          inFlightRef.current = false;
          if (!silent) setLoading(false);
          // Mark that at least one status fetch has settled, so consumers can
          // distinguish "not fetched yet" from "fetched, nothing connected".
          setInitialized(true);
        }
      },
      [apiBase, userId]
    );
  
    const setChannelConnection = useCallback((key, isConnected) => {
      setConnections((prev) => ({ ...prev, [key]: !!isConnected }));
    }, []);
  
    useEffect(() => {
      // Logout / no userId: reset state and allow future initial fetch. There
      // is nothing to fetch, so treat connections as initialized immediately.
      if (!userId) {
        fetchedForUserIdRef.current = null;
        setConnections(DEFAULT_CONNECTIONS);
        setLoading(false);
        setInitialized(true);
        setLastError(null);
        return;
      }
  
      // Initial load once per userId.
      if (fetchedForUserIdRef.current === userId) return;
      refreshConnections();
    }, [userId, refreshConnections]);
  
    const value = useMemo(
      () => ({
        userId,
        connections,
        setConnections,
        setChannelConnection,
        refreshConnections,
        loading,
        initialized,
        lastError,
      }),
      [userId, connections, setChannelConnection, refreshConnections, loading, initialized, lastError]
    );
  
    return (
      <AccountSetupContext.Provider value={value}>
        {children}
      </AccountSetupContext.Provider>
    );
  }
  
  export function useAccountSetup() {
    const ctx = useContext(AccountSetupContext);
    if (!ctx) {
      throw new Error("useAccountSetup must be used within an AccountSetupProvider");
    }
    return ctx;
  }
  
  